import React, { ComponentType, useCallback, useMemo, useState } from "react";

import type { Dayjs } from "dayjs";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  type ChartOptions,
} from "chart.js";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import RebetLoaderJs from "../../../shared/RebetLoader/RebetLoader";
import Select from "../../../shared/Select/Select";
import {
  formatDateNoTimezone,
  getAdminNow,
} from "../../../../utils/dateService";
import {
  formatCompactCurrencyTick,
  formatCurrency,
} from "../../../../utils/financialFormatters";
import { useGetFinancialsMonthly } from "../../../../queries/financials/useGetFinancialsMonthly";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";

import styles from "./MonthlyTotals.module.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;
const RebetLoader = RebetLoaderJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

const METRIC_OPTIONS = [
  { value: "total_revenue", label: "Total Revenue" },
  { value: "total_purchases", label: "Total Purchases" },
  { value: "total_withdrawals", label: "Total Withdrawals" },
] as const;

type MetricKey = (typeof METRIC_OPTIONS)[number]["value"];

const BAR_COLOR = "#6FCF97";

const MonthlyTotals: React.FC = () => {
  // Default range is the last 12 months from today: start at the 1st of the
  // month 11 months ago (so the chart spans 12 calendar months including the
  // current one) and end at today (`endOf("day")`). Using `endOf("month")`
  // would push the range past the present and pad the chart with a future
  // bar that never has data.
  const [dates, setDates] = useState<DateRange>(() => {
    const now = getAdminNow();
    return [now.subtract(11, "month").startOf("month"), now.endOf("day")];
  });
  const [selectedMetric, setSelectedMetric] =
    useState<MetricKey>("total_revenue");

  const currencySymbol = useCurrencySymbol() ?? "$";

  const dateFilters = useMemo(
    () => ({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    }),
    [dates],
  );

  const { data: points = [], isFetching, isLoading, isError } =
    useGetFinancialsMonthly(dateFilters);

  const selectedLabel = useMemo(
    () =>
      METRIC_OPTIONS.find((o) => o.value === selectedMetric)?.label ??
      "Total Revenue",
    [selectedMetric],
  );

  const getData = useCallback((_queryString: string | null) => {
    // Date changes flow through `dates` -> query key, which refetches.
  }, []);

  const chartData = useMemo(
    () => ({
      labels: points.map((p) => formatDateNoTimezone(p.month, "MMM YYYY")),
      datasets: [
        {
          label: selectedLabel,
          data: points.map((p) => p[selectedMetric]),
          backgroundColor: BAR_COLOR,
          borderRadius: 4,
          maxBarThickness: 36,
        },
      ],
    }),
    [points, selectedMetric, selectedLabel],
  );

  const chartOptions = useMemo<ChartOptions<"bar">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: "#9aa3b1", font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(255,255,255,0.06)" },
          ticks: {
            color: "#9aa3b1",
            font: { size: 11 },
            callback: (value: number | string) =>
              formatCompactCurrencyTick(Number(value), currencySymbol),
          },
        },
      },
      plugins: {
        legend: { display: false },
        title: { display: false },
        datalabels: { display: false },
        tooltip: {
          enabled: true,
          mode: "index" as const,
          intersect: false,
          backgroundColor: "#252729",
          padding: 12,
          displayColors: true,
          titleFont: { family: "Poppins-regular", size: 13, weight: 600 },
          bodyFont: { family: "Poppins-regular", size: 13 },
          callbacks: {
            title: (items: { dataIndex: number }[]) => {
              if (!items?.length) return "";
              const iso = points[items[0].dataIndex]?.month;
              return iso ? formatDateNoTimezone(iso, "MMMM, YYYY") : "";
            },
            label: (item: { raw: unknown }) =>
              formatCurrency(Number(item.raw), currencySymbol),
          },
        },
      },
    }),
    [points, currencySymbol],
  );

  const exportRows = useMemo(
    () =>
      points.map((p) => ({
        month: p.month,
        metric: selectedLabel,
        value: p[selectedMetric],
      })),
    [points, selectedMetric, selectedLabel],
  );

  const showLoading = isLoading || isFetching;

  return (
    <section className={styles.card} aria-label={`${selectedLabel} by month`}>
      <header className={styles.header}>
        <Select
          className={styles.metricSelect}
          value={selectedMetric}
          options={METRIC_OPTIONS as unknown as { value: string; label: string }[]}
          onChange={(value) => setSelectedMetric(value as MetricKey)}
          aria-label="Metric"
        />
        <div className={styles.controls}>
          <CustomDatePicker
            getData={getData}
            dates={dates}
            setDates={setDates}
            showPresets={true}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename="monthly-totals.xlsx"
          />
        </div>
      </header>

      <div className={styles.chartContainer}>
        {showLoading ? (
          <div className={styles.loaderOverlay} data-testid="chart-loader">
            <RebetLoader width={100} height={100} />
          </div>
        ) : isError ? (
          <p
            className={styles.errorBanner}
            role="alert"
            data-testid="monthly-totals-error"
          >
            {AppStrings.FINANCIALS_MONTHLY_LOAD_ERROR}
          </p>
        ) : points.length === 0 ? (
          <p className={styles.emptyMessage} data-testid="monthly-totals-empty">
            {AppStrings.FINANCIALS_MONTHLY_EMPTY_STATE}
          </p>
        ) : (
          <Bar data={chartData} options={chartOptions} />
        )}
      </div>
    </section>
  );
};

export default MonthlyTotals;
